import { useEffect } from "react"
import { batch, useSignal } from "@preact/signals-react"
import { useAppToast } from "@ui-seeker/app"
import { getSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS } from "@constants"
import { isLoadingSchedules, schedulesTableData } from "../@signals"


const FREQUENCY_LABELS = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' }

// The table needs the cadence as one sortable/searchable string; 'custom' alone
// hides the very number that defines it.
const frequencyLabel = s => s.frequency === 'custom'
    ? (s.everyNDays ? `Every ${s.everyNDays} days` : 'Custom')
    : (FREQUENCY_LABELS[s.frequency] || s.frequency)


export default function useSchedulesData() {
    const { showToast } = useAppToast()
    const isEmpty = useSignal(false)

    const fetchSchedules = () => {
        schedulesTableData.value = []
        // detection=1 asks the back for the "is this schedule detecting anything" counters
        // (produced batches/tasks, and new products of its brands with no attempt). It is
        // opt-in because it scans DIPI_SKU_MAPPING over a window, and this endpoint is also
        // called by things that only want the list.
        getSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/schedules`, { detection: 1 })
            .then(res => {
                if (!res?.schedules?.length) isEmpty.value = true
                else isEmpty.value = false
                schedulesTableData.value = (res?.schedules || []).map(s => ({
                    ...s,
                    frequencyLabel: frequencyLabel(s),
                    // Flattened for the table: MUIDataTable reads one value per column.
                    // `null` and `0` mean different things here — null is "this direction
                    // cannot be answered brand-scoped", zero is "nothing pending" — so the
                    // nullish coalescing must NOT collapse them.
                    unattempted: s.detection ? s.detection.unattempted : null,
                    seen: s.detection ? s.detection.seen : null,
                }))
            })
            .catch(() => showToast.error('Failed to load schedules'))
            .finally(() => {
                isLoadingSchedules.value = false
            })
    }

    useEffect(() => {
        if (isLoadingSchedules.value) batch(() => {
            fetchSchedules()
        })
    }, [isLoadingSchedules.value])

    const refreshSchedules = () => {
        isLoadingSchedules.value = true
    }

    return {
        isEmpty: isEmpty.value,
        refreshSchedules
    }
}
